import styles from './CTAStandard.module.scss';
import { Colors } from '../../../../theme/Colors';
import { CTATextContent } from '../CTATextContent';
import { BgColor, DeprecatedBgColor, CTABtnColor } from '../types';
import { getBgColor } from '../utils';
import type { AnchorTarget } from '../../../../ts-types';
import { LegacyColorMap } from '@/theme/LegacyColorMap';

export type Props = {
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white' | 'zapier'
   * | 'night' | 'moss' | 'neutral800' | 'neutral100' | 'earth'`.
   *
   * It also controls the color of the CTA button - background color
   * is white or neutral100, then the CTA button is `primary`. Otherwise, it is `primary-alt`
   * */
  backgroundColor?: BgColor | DeprecatedBgColor;

  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Subheading text content displayed below the heading.
   */
  subHeading: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Color of the CTA button.
   *
   * Must be one of the type `'primary' | 'primary-alt' | 'primary-dark' | 'brand'`
   */
  ctaBtnColor?: CTABtnColor;
  /**
   * Text displayed above the CTA button.
   */
  description?: React.ReactNode;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component.
   */
  children?: React.ReactNode;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
};

// TODO: Make sure not to render Button when Subscribe form is implemented and used as a children

/**
 * `CTAStandard` is used to direct our customers to take a specific action. It features a heading, description text, button, link, and media, which is rendered as `children`.
 */
export const CTAStandard = ({
  backgroundColor = 'white',
  heading,
  subHeading,
  ctaText,
  ctaHref,
  ctaBtnColor,
  description,
  onCtaClick,
  onCtaKeyDown,
  children,
  maxWidth = 1170,
  ctaTarget,
}: Props) => {
  const bgColor = getBgColor(backgroundColor);

  return (
    <div
      className={styles.root}
      style={{
        '--_zds-cta-standard-bg-color': Colors[LegacyColorMap[bgColor]],
      }}
      data-zds
    >
      <div
        className={styles.content}
        style={{
          '--_zds-cta-standard-max-width': `${maxWidth}px`,
        }}
        data-zds
      >
        <CTATextContent
          heading={heading}
          subHeading={subHeading}
          description={description}
          ctaText={ctaText}
          ctaHref={ctaHref}
          ctaBtnColor={ctaBtnColor}
          onCtaClick={onCtaClick}
          onCtaKeyDown={onCtaKeyDown}
          bgColor={bgColor}
          ctaTarget={ctaTarget}
        />
        <div
          className={styles.mediaContent}
          data-testid="media-content"
          data-zds
        >
          {children}
        </div>
      </div>
    </div>
  );
};
